import React from 'react';
import {useIntl} from 'react-intl';
import {upperFirst} from 'services';
import {useMethodSubcategories} from 'services/query/modules';

export type UseMethodFiltersProps = {
  selectedCategories?: string[];
};

export const useMethodFilters = (props?: UseMethodFiltersProps) => {
  const intl = useIntl();

  const {selectedCategories = []} = props || {};

  const {data: allSubcategories = []} = useMethodSubcategories();

  const categoryIdMap: Record<number, {category: ToolboxObject.Category; subcategories: ToolboxObject.Subcategory[]}> = {};
  allSubcategories.forEach((subcategory) => {
    const {category} = subcategory;

    if (!categoryIdMap[category.id]) {
      categoryIdMap[category.id] = {
        category,
        subcategories: [subcategory],
      };
    } else {
      categoryIdMap[category.id].subcategories.push(subcategory);
    }
  });
  const categories = React.useMemo(
    () =>
      Object.values(categoryIdMap).map(({category, subcategories}) => {
        const categoryKey = category.name.split(' ').join('_').toLowerCase();
        return {
          label: intl.formatMessage({id: `pages.library.category.${categoryKey}`, defaultMessage: upperFirst(category.name)}),
          value: category.id.toString(),
          subcategories,
        };
      }),
    [intl, categoryIdMap]
  );

  const subcategories = React.useMemo(
    () =>
      [
        ...allSubcategories.map((subcategory) => {
          const subcategoryKey = subcategory.name.split(' ').join('_').toLowerCase();

          const category = subcategory.category.id.toString();
          return {
            label: intl.formatMessage({id: `pages.library.subcategory.${subcategoryKey}`, defaultMessage: upperFirst(subcategory.name)}),
            value: subcategory.id.toString(),
            category,
            isActive: !selectedCategories.length || selectedCategories.includes(category),
          };
        }),
      ]
        // Put active subcategories on top, ASC sort by catergory
        .sort((a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1) || +a.category - +b.category),
    [intl, allSubcategories, selectedCategories]
  );

  const socialForms = React.useMemo(
    () => [
      {
        value: '1',
        label: intl.formatMessage({id: `pages.library.socialForm.1_on_1`, defaultMessage: '1 on 1'}),
      },
      {
        value: '2',
        label: intl.formatMessage({id: `pages.library.socialForm.group`, defaultMessage: 'Group'}),
      },
    ],
    [intl]
  );

  const formats = React.useMemo(
    () => [
      {
        label: intl.formatMessage({id: `pages.library.format.easy`, defaultMessage: 'Easy'}),
        value: '1',
      },
      {
        label: intl.formatMessage({id: `pages.library.format.medium`, defaultMessage: 'Medium'}),
        value: '2',
      },
      {
        label: intl.formatMessage({id: `pages.library.format.hard`, defaultMessage: 'Hard'}),
        value: '3',
      },
    ],
    [intl]
  );

  return [categories, subcategories, socialForms, formats] as const;
};
